/* global Sys, Core, Resources */
Sys.ns("Core");

/**
 * The Money Manager model.
 *
 * @class Core.MoneyManagerModel
 * @extends Core.Model
 */
Core.MoneyManagerModel = {

    /** @inheritdoc */
    constructor: function() {
        Core.MoneyManagerModel.superclass.constructor.apply(this, arguments);
    },

    /** @inheritdoc */
    setupData: function() {
        var initResponse = Resources.readData("gameServerInitResponse");

        this.setupMoneyHandling();
        this.setBalance(Number(initResponse.credit));

        this.storeData("betlineCoins", this.getBetlineCoinValue(initResponse));
        this.storeData("inFreeRounds", parseInt(initResponse.freeRoundsLeft, 10) > 0);
        this.storeData("netPosition", 0);

        this.calculateMinimumBetInCents();
    },

    /**
     * Setup money handling.
     *
     * @private
     * @throws {Error} If currency format is missing.
     * @returns {void}
     */
    setupMoneyHandling: function() {
        var initResponse = Resources.readData("gameServerInitResponse"),
            moneyFormats = {
                player: Sys.utils.XMLHelper.getMoneyFormatFromXML(Resources.readData("moneyformat_player"), initResponse.playercurrencyiso),
                jackpot: Sys.utils.XMLHelper.getMoneyFormatFromXML(Resources.readData("moneyformat_jackpot"), initResponse.jackpotcurrencyiso)
            };

        if (!moneyFormats.player) {
            throw new Error("The player currency format is missing!");
        }

        if (!moneyFormats.jackpot) {
            throw new Error("The jackpot currency format is missing!");
        }

        this.storeData("moneyFormats", moneyFormats);
    },

    /**
     * Utility function to get the proper money format.
     *
     * @param {string} [format=player] The (optional) currency format to use (player|jackpot).
     * @returns {Object} The corresponding money format object.
     */
    getMoneyFormat: function(format) {
        var moneyFormats = this.readData("moneyFormats");

        return moneyFormats[format] ? moneyFormats[format] : moneyFormats.player;
    },

    /**
     * @deprecated Not in use.
     * @param {string} [format=player] The (optional) currency format to use (player|jackpot).
     * @returns {string} The currency.
     */
    getCurrency: function(format) {
        return this.getMoneyFormat(format).currencyChar;
    },

    /**
     * Set the new balance in cents.
     *
     * @param {number|string} rawCents The number of cents, might contains decimals.
     * @returns {boolean} If operation was successful.
     */
    setBalance: function(rawCents) {
        // Note: This is a temporary fix until the source of this problem is found.
        // We will now allow cents with decimals and truncate them by flooring the value.
        // See PRODBUG-1225 for more information. https://jira.netent.com/browse/PRODBUG-1225.

        // Floor the number to get rid of any decimals.
        var cents = parseInt(rawCents, 10);

        // Only allow positive numbers
        if (!isNaN(cents) && cents >= 0) {
            this.storeData("playerBalanceCents", cents);

            // Update the available number of coins
            this.storeData("playerBalanceCoins", Math.floor(cents / this.readData("denomination")));
            return true;
        }
        return false;
    },

    /**
     * Set the bet level.
     *
     * @param {number} betLevel Bet level.
     * @returns {boolean} If operation was successful.
     */
    setBetLevel: function(betLevel) {
        var betInCoins;

        if (betLevel > 0 && Math.floor(betLevel) === betLevel) {
            this.storeData("betLevel", betLevel);

            betInCoins = betLevel * this.readData("betlineCoins");

            this.storeData("playerBetCoins", betInCoins);
            this.storeData("playerBetCents", betInCoins * this.readData("denomination"));

            return true;
        }
        return false;
    },

    /**
     * Set the denomination.
     *
     * @param {number} denomination Denomination.
     * @returns {boolean} If operation was successful.
     */
    setDenomination: function(denomination) {
        if (denomination > 0) {
            this.storeData("denomination", denomination);
            this.storeData("playerBetCents", this.readData("playerBetCoins") * denomination);
            this.storeData("playerBalanceCoins", Math.floor(this.readData("playerBalanceCents") / denomination));

            return true;
        }
        return false;
    },

    /**
     * Get the player balance in cents.
     * NOTE: Overridden for OpenBet.
     *
     * @deprecated Not in use.
     * @returns {number} The player balance in cents.
     */
    getBalanceCents: function() {
        return this.readData("playerBalanceCents");
    },

    /**
     * Get the number of coins available to the player (based on cash balance and bet settings).
     *
     * @deprecated Not in use.
     * @returns {number} The number of coins available to the player.
     */
    getBalanceCoins: function() {
        return this.readData("playerBalanceCoins");
    },

    /**
     * Get the number of cents placed for each bet.
     *
     * @deprecated Not in use.
     * @returns {number} The number of cents placed for each bet.
     */
    getBetCents: function() {
        return this.readData("playerBetCents");
    },

    /**
     * Get the number of coins placed for each bet (based on the bet level and betline coin value).
     *
     * @deprecated Not in use.
     * @returns {number} The number of coins placed for each bet.
     */
    getBetCoins: function() {
        return this.readData("playerBetCoins");
    },

    /**
     * Parses bet lines values to get the total number of coins for all bet lines.
     *
     * @private
     * @param {Object} initResponse The init server response.
     * @returns {number} The total number of coins for all the betlines.
     */
    getBetlineCoinValue: function(initResponse) {
        var betlines = initResponse["bl.standard"].split(","),
            numBetlines = betlines.length,
            betlineCoins = 0,
            i;

        // Calculate the bet coins by summing up all bets on each betline, because player is betting on all bet lines.
        // For example: if a game has 25 betlines with a bet value of 1 on each betline the total betline coin value
        // will be 25.
        for (i = 0; i < numBetlines; i++) {
            betlineCoins += parseInt(initResponse["bl.i" + i + ".coins"], 10);
        }

        return betlineCoins;
    },

    /**
     * Calculates the minimum bet in cents.
     *
     * @private
     * @returns {void}
     */
    calculateMinimumBetInCents: function() {
        var initResponseObject = Resources.readData("gameServerInitResponseObject"),
            betLevel = initResponseObject.betlevel.all,
            denomination = initResponseObject.denomination.all,
            betLevelClone = this.validateArray(betLevel),
            denominationClone = this.validateArray(denomination),
            betlineCoins = this.readData("betlineCoins"),
            minimumBetInCents = betLevelClone[0] * betlineCoins * denominationClone[0];

        this.storeData("minimumBetInCents", minimumBetInCents);
    },

    /**
     * Validates the array, clones and sorts it.
     *
     * @private
     * @param {Array|number} array The array or number to be validated.
     * @returns {Array} The sorted and validated array.
     */
    validateArray: function(array) {
        if (!Sys.isArray(array)) {
            return [array];
        }

        return array.slice(0).sort(function(a, b) {
            return a - b;
        });
    },

    /**
     * Sets the minimum bet available, in cents.
     *
     * @private
     * @deprecated Not in use.
     * @param {number} cents The minimum value to bet.
     * @returns {void}
     */
    setMinimumBetInCents: function(cents) {
        this.storeData("minimumBetInCents", cents);
    },

    /**
     * Method to get the minimum bet that can be placed, in cents.
     *
     * @private
     * @deprecated Not in use.
     * @returns {number} Minimum bet, in cents.
     */
    getMinimumBetInCents: function() {
        return this.readData("minimumBetInCents");
    },

    /**
     * Get the netPosition.
     *
     * @returns {number} The number of cents calculated from total win and bets placed in the same game round.
     */
    getNetPosition: function() {
        return this.readData("netPosition");
    },

    /**
     * Sets the netPosition.
     *
     * @param {number} value The value that will be set to netPosition.
     * @returns {void}
     */
    setNetPosition: function(value) {
        this.storeData("netPosition", value);
    }
};

Core.MoneyManagerModel = Sys.extend(Core.Model, Core.MoneyManagerModel, "Core.MoneyManagerModel");
